/**
 * Call Resilience Utilities
 * Inspired by WhatsApp VoIP architecture:
 * 1. Opus In-Band Forward Error Correction (FEC) and Discontinuous Transmission (DTX)
 * 2. Audio packet prioritization across OS/network queues
 * 3. Real-time WebRTC stats monitoring and network degradation detection
 * 4. Automatic video bitrate throttling to protect the voice stream during network drops
 */

/**
 * Optimizes the SDP to enforce Opus Forward Error Correction (FEC),
 * DTX (discontinuous transmission), variable bitrate, and bit-rate constraints.
 * This prevents audio stutter, robotic noise, and dropouts over lossy cellular/Wi-Fi.
 */
export const optimizeAudioSdp = (sdp) => {
  if (!sdp || typeof sdp !== 'string') return sdp;

  // Find Opus payload type (e.g. "a=rtpmap:111 opus/48000/2")
  const opusPtMatch = sdp.match(/a=rtpmap:(\d+)\s+opus\/48000\/2/i);
  if (!opusPtMatch) return sdp;

  const pt = opusPtMatch[1];
  const fmtpRegex = new RegExp(`a=fmtp:${pt}\\s+([^\r\n]*)`, 'i');
  const fmtpMatch = sdp.match(fmtpRegex);

  const desiredParams = {
    minptime: '10',
    useinbandfec: '1', // Forward Error Correction: redundant packets to recover lost audio
    usedtx: '1', // Discontinuous Transmission: conserves bandwidth during speech pauses
    maxaveragebitrate: '32000', // Adaptive Opus bitrate (can scale down to 6-8kbps on bad networks)
    cbr: '0', // Variable Bitrate (VBR)
    stereo: '0',
    'sprop-stereo': '0',
  };

  if (fmtpMatch) {
    const existingParamsStr = fmtpMatch[1] || '';
    const paramMap = {};
    existingParamsStr.split(';').forEach((part) => {
      const trimmed = part.trim();
      if (!trimmed) return;
      const [k, ...v] = trimmed.split('=');
      paramMap[k.trim()] = v.join('=').trim();
    });

    Object.assign(paramMap, desiredParams);

    const formattedFmtp = `a=fmtp:${pt} ` + Object.entries(paramMap)
      .map(([k, v]) => (v !== '' ? `${k}=${v}` : k))
      .join(';');

    return sdp.replace(fmtpRegex, formattedFmtp);
  }

  // Insert fresh a=fmtp line directly after the a=rtpmap line
  const rtpmapLine = opusPtMatch[0];
  const newFmtp = `a=fmtp:${pt} ` + Object.entries(desiredParams)
    .map(([k, v]) => `${k}=${v}`)
    .join(';');

  return sdp.replace(rtpmapLine, `${rtpmapLine}\r\n${newFmtp}`);
};

/**
 * Prioritizes the audio sender over all other network traffic on the device.
 */
export const prioritizeAudioSender = (pc) => {
  if (!pc || typeof pc.getSenders !== 'function') return;
  try {
    const senders = pc.getSenders();
    const audioSender = senders.find((s) => s.track && s.track.kind === 'audio');
    if (audioSender && audioSender.getParameters && audioSender.setParameters) {
      const params = audioSender.getParameters();
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [{}];
      }
      params.encodings.forEach((enc) => {
        enc.priority = 'high';
        enc.networkPriority = 'high';
      });
      audioSender.setParameters(params).catch(() => {});
    }
  } catch (err) {
    console.warn('[CallResilience] Could not set audio priority:', err);
  }
};

/**
 * Dynamically adapts video bitrate and resolution based on network quality.
 * When quality is poor, video is throttled to 120 kbps so 100% of available
 * bandwidth is preserved for the voice stream.
 */
export const adaptVideoSender = (pc, quality) => {
  if (!pc || typeof pc.getSenders !== 'function') return;
  try {
    const senders = pc.getSenders();
    const videoSender = senders.find((s) => s.track && s.track.kind === 'video');
    if (videoSender && videoSender.getParameters && videoSender.setParameters) {
      const params = videoSender.getParameters();
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [{}];
      }
      params.encodings.forEach((enc) => {
        if (quality === 'poor') {
          enc.maxBitrate = 120000; // 120 kbps
          enc.maxFramerate = 15;
          enc.scaleResolutionDownBy = 2.0;
        } else {
          delete enc.maxBitrate;
          delete enc.maxFramerate;
          delete enc.scaleResolutionDownBy;
        }
      });
      videoSender.setParameters(params).catch(() => {});
    }
  } catch (err) {
    console.warn('[CallResilience] Could not adapt video parameters:', err);
  }
};

/**
 * Creates a lightweight monitor using pc.getStats() to detect packet loss and high latency.
 */
export const createCallQualityMonitor = (pc, onQualityChange) => {
  if (!pc || typeof pc.getStats !== 'function') return () => {};

  let lastPacketsLost = 0;
  let lastPacketsTotal = 0;
  let intervalId = null;

  const check = async () => {
    try {
      if (pc.connectionState !== 'connected') return;
      const stats = await pc.getStats();
      let currentPacketsLost = 0;
      let currentPacketsTotal = 0;
      let currentRtt = 0;

      stats.forEach((report) => {
        if (report.type === 'inbound-rtp' && report.kind === 'audio') {
          currentPacketsLost = report.packetsLost || 0;
          currentPacketsTotal = (report.packetsReceived || 0) + currentPacketsLost;
        }
        if (report.type === 'candidate-pair' && (report.state === 'succeeded' || report.nominated)) {
          if (typeof report.currentRoundTripTime === 'number') {
            currentRtt = report.currentRoundTripTime;
          }
        }
      });

      const deltaLost = Math.max(0, currentPacketsLost - lastPacketsLost);
      const deltaTotal = Math.max(0, currentPacketsTotal - lastPacketsTotal);

      lastPacketsLost = currentPacketsLost;
      lastPacketsTotal = currentPacketsTotal;

      const lossRatio = deltaTotal > 0 ? deltaLost / deltaTotal : 0;

      if (lossRatio > 0.12 || currentRtt > 0.55) {
        onQualityChange('poor', { lossRatio, rtt: currentRtt });
        adaptVideoSender(pc, 'poor');
      } else if (lossRatio < 0.04 && currentRtt < 0.3) {
        onQualityChange('good', { lossRatio, rtt: currentRtt });
        adaptVideoSender(pc, 'good');
      }
    } catch (_) {}
  };

  intervalId = setInterval(check, 3000);
  return () => {
    if (intervalId) clearInterval(intervalId);
  };
};
